/*
 * The frame around every signed-in screen, as in Jobber: the side menu (Create on top, then Home and Schedule, Work, Sales,
 * Money; the signed-in person and their role on top), and the top bar with the company picker on the left, then search,
 * messages, the bell and the gear (Settings, the team, language, look and Log out).
 * Menu items a role cannot use are left out. The arrow at the bottom shrinks the menu to icons and back, as in Jobber.
 * On phones the menu slides in from the right (owner, 2026-10-29), and a round + button makes things, like the old Steward.
 */
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { isClientAccount, type Action, type Trade } from "@steward/shared";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { api } from "../api/client";
import { useToCheckCount } from "../api/money";
import { useLiveSpot, useMyPayAccess } from "../api/payroll";
import { useAlerts, useInboxUnread, useMyPerson, useReadAlerts } from "../api/hooks";
import { ActivityFeed } from "../components/ActivityFeed";
import { CommandMenu } from "../components/CommandMenu";
import { MAPS_NAMES, readMapsPick, saveMapsPick } from "../components/Directions";
import { Icon, type IconName } from "../components/Icon";
import { Menu } from "../components/overlays";
import { ProfilePhotoModal } from "../components/ProfilePhoto";
import { Avatar, ROLE_WORDS } from "../components/parts";
import { payWords, shortName } from "@steward/shared";
import { crewWords, useSession } from "./session";
import { useQueryClient } from "@tanstack/react-query";
import { markSignedOut } from "./sso";
import { unzoom } from "./fit-wide";
import { WorkingFor } from "./working-for";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  action?: Action;
  /** Not built yet: which build brings it. */
  soon?: string;
  /** Only at HQ or a brand (head office pages look at the companies below). */
  above?: boolean;
  /** Not at The Trades (HQ): the home service work lives in the brands and locations (owner, 2026-10-28). */
  notHq?: boolean;
  /** Opens on this page instead when the role can see it (HR opens on Payroll, owner 2026-11-02). */
  first?: { to: string; action: Action };
  /** Only for this trade (Proposals are commercial cleaning bids). */
  trade?: string;
}

const GROUPS: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: "/home", label: "Home", icon: "home", action: "company.read" },
      { to: "/schedule", label: "Schedule", icon: "schedule", action: "schedule.read", notHq: true },
    ],
  },
  {
    title: "Work",
    items: [
      { to: "/requests", label: "Requests", icon: "requests", action: "lead.read", notHq: true },
      { to: "/quotes", label: "Quotes", icon: "quotes", action: "quote.read", notHq: true },
      { to: "/proposals", label: "Proposals", icon: "file", action: "quote.read", notHq: true, trade: "cleaning" },
      { to: "/jobs", label: "Jobs", icon: "jobs", action: "job.read", notHq: true },
      { to: "/invoices", label: "Invoices", icon: "invoices", action: "invoice.read", notHq: true },
    ],
  },
  {
    title: "Sales",
    items: [
      { to: "/clients", label: "Clients", icon: "clients", action: "client.read", notHq: true },
      { to: "/marketing", label: "Marketing", icon: "marketing", action: "client.bulk", notHq: true },
      { to: "/receptionist", label: "Receptionist", icon: "sparkle", action: "settings.read", notHq: true },
    ],
  },
  {
    title: "Money",
    items: [
      { to: "/payments", label: "Payments", icon: "payments", action: "invoice.read", notHq: true },
      { to: "/books", label: "Books", icon: "books", action: "books.read" },
      {
        to: "/hr/team",
        label: "HR",
        icon: "team",
        action: "people.read",
        first: { to: "/hr/payroll", action: "payroll.read" },
      },
      { to: "/insights", label: "Insights", icon: "insights", action: "insights.read" },
    ],
  },
  {
    title: "Head office",
    items: [
      { to: "/head-office", label: "Head office", icon: "building", action: "hq.read", above: true },
      { to: "/security", label: "Security", icon: "shield", action: "security.read" },
    ],
  },
  {
    items: [
      { to: "/my-day", label: "Home", icon: "home", action: "myday.read" },
      { to: "/my-schedule", label: "Schedule", icon: "schedule", action: "myday.read" },
      { to: "/my-time-off", label: "Time off", icon: "clock", action: "myday.read" },
      { to: "/my-pay", label: "My pay", icon: "money", action: "mypay.read" },
    ],
  },
];

/** The agency's menu (draft 13, HubSpot style): no schedule, requests, jobs, visits or crew screens. */
const AGENCY_GROUPS: { title?: string; items: NavItem[] }[] = [
  { items: [{ to: "/home", label: "Home", icon: "home", action: "company.read" }] },
  {
    title: "Sales",
    items: [
      { to: "/deals", label: "Deals", icon: "pipeline", action: "lead.read" },
      { to: "/proposals", label: "Proposals", icon: "quotes", action: "quote.read" },
      { to: "/clients", label: "Clients", icon: "clients", action: "client.read" },
    ],
  },
  {
    title: "Work",
    items: [
      { to: "/tasks", label: "Tasks", icon: "checklist", action: "client.read" },
      { to: "/projects", label: "Projects", icon: "jobs", action: "job.read" },
      { to: "/results", label: "Results", icon: "insights", action: "client.read" },
      { to: "/marketing", label: "Marketing", icon: "marketing", action: "client.bulk" },
    ],
  },
  {
    title: "Money",
    items: [
      { to: "/invoices", label: "Invoices", icon: "invoices", action: "invoice.read" },
      { to: "/books", label: "Books", icon: "books", action: "books.read" },
      { to: "/hr/team", label: "Team", icon: "team", action: "people.read" },
      { to: "/insights", label: "Insights", icon: "insights", action: "insights.read" },
    ],
  },
  { title: "Head office", items: [{ to: "/security", label: "Security", icon: "shield", action: "security.read" }] },
];

/** A client's marketing account (draft 13 client accounts, 2026-11-04): Marketing is all it has. */
const CLIENT_GROUPS: { title?: string; items: NavItem[] }[] = [
  { items: [{ to: "/marketing", label: "Marketing", icon: "marketing", action: "client.bulk" }] },
];

/** Pages a client's marketing account opens; anything else goes to its Marketing. */
const CLIENT_PAGES = ["/marketing", "/settings"];

function groupsFor(trade: string) {
  if (trade === "agency") return AGENCY_GROUPS;
  return isClientAccount(trade) ? CLIENT_GROUPS : GROUPS;
}

export function Shell() {
  const { me, company, companies, role, can, switchCompany } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useRef<HTMLInputElement>(null);
  const [finder, setFinder] = useState<{ open: boolean; start: string }>({ open: false, start: "" });
  const crew = role === "crew";
  const words = crewWords(me.locale);
  const access = useMyPayAccess(company.id, can("mypay.read"));
  const myPay = Boolean(access.data?.linked && access.data.on);
  const [slim, setSlim] = useState(readSlim);
  const [photoOpen, setPhotoOpen] = useState(false);
  const mine = useMyPerson(company.id);
  // On the clock with Live GPS on: the phone sends its spot for the Home map.
  useLiveSpot(company.id, can("clock.update") && Boolean(mine.data));
  const toggleSlim = () => {
    const next = !slim;
    try {
      localStorage.setItem("steward.menu", next ? "slim" : "wide");
    } catch {
      // Not saved; the choice lasts until the page closes.
    }
    setSlim(next);
  };

  useEffect(() => setMenuOpen(false), [path]);
  const clientAccount = isClientAccount(company.trade);
  useEffect(() => {
    if (clientAccount && !CLIENT_PAGES.some((p) => path === p || path.startsWith(`${p}/`))) void navigate({ to: "/marketing" });
  }, [clientAccount, path, navigate]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setFinder({ open: true, start: "" });
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        setFinder({ open: true, start: "" });
      } else if ((e.key === "c" || e.key === "C") && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // C opens the Create menu, as its tooltip says.
        const create = document.querySelector<HTMLButtonElement>("[data-create]");
        if (create) {
          e.preventDefault();
          create.click();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => setFinder((f) => (f.open ? { ...f, open: false } : f)), [path]);

  const signOut = async () => {
    markSignedOut();
    await api("POST", "auth/sign-out").catch(() => null);
    qc.clear();
    await navigate({ to: "/sign-in" });
  };

  const isActive = (to: string) =>
    path === to || path.startsWith(`${to.split("/").slice(0, 2).join("/")}/`) || (to.startsWith("/hr") && path.startsWith("/hr"));
  const link = (i: NavItem) => {
    const label =
      i.to === "/my-day"
        ? words.home
        : i.to === "/my-schedule"
          ? words.schedule
          : i.to === "/my-time-off"
            ? words.timeOff
            : i.to === "/my-pay"
              ? payWords(me.locale).myPay
              : i.label;
    return (
      <Link
        key={i.to}
        to={i.first && can(i.first.action) ? i.first.to : i.to}
        className="navlink"
        data-status={isActive(i.to) ? "active" : undefined}
        data-tip={slim ? label : undefined}
      >
        <Icon name={i.icon} />
        <span className="label">{label}</span>
        {i.to === "/invoices" ? <ToCheckCount /> : null}
        {i.soon ? <span className="soon">{i.soon === "later" ? "Later" : "Soon"}</span> : null}
      </Link>
    );
  };
  // Crew get their own short menu (My day, My pay); the office gets everything their role allows. My pay shows for anyone
  // on the team list, owners too (question 49), unless the office turned their My pay off on Team (question 50).
  const visible = (i: NavItem) =>
    i.to === "/my-pay"
      ? myPay
      : crew
        ? i.to === "/my-day" || i.to === "/my-schedule" || i.to === "/my-time-off"
        : i.to !== "/my-day" &&
          i.to !== "/my-schedule" &&
          i.to !== "/my-time-off" &&
          (!i.action || can(i.action)) &&
          (!i.above || company.level !== "location") &&
          !(i.notHq && company.level === "hq") &&
          (!i.trade || company.trade === i.trade);

  return (
    <div className={`frame${slim ? " slim" : ""}`}>
      <nav className={`side${menuOpen ? " open" : ""}`} aria-label="Main">
        <div className="brandmark">
          <span className="logo" aria-hidden>
            {initialsOf(me.name)}
          </span>
          <div className="who">
            <b>{personShort(me.name)}</b>
            <span>{ROLE_WORDS[role] ?? role}</span>
          </div>
        </div>
        {!crew && !clientAccount && can("client.update") ? <CreateMenu slim={slim} /> : null}
        {groupsFor(company.trade).map((g, gi) => {
          const items = g.items.filter(visible);
          if (!items.length) return null;
          return (
            <div className="navgroup" key={gi}>
              {g.title ? <h3>{g.title}</h3> : null}
              {items.map(link)}
            </div>
          );
        })}
        <div className="bottom">
          <button
            type="button"
            className="rail-toggle"
            aria-label={slim ? "Widen menu" : "Shrink menu"}
            aria-pressed={slim}
            data-tip={slim ? "Widen menu" : "Shrink menu"}
            onClick={toggleSlim}
          >
            <Icon name={slim ? "arrowRight" : "arrowLeft"} size={18} />
          </button>
        </div>
      </nav>
      {menuOpen ? <div className="scrim" style={{ zIndex: 1000 }} onClick={() => setMenuOpen(false)} /> : null}

      <div className="main">
        <header className="topbar">
          <button type="button" className="iconbtn menu-toggle" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <Icon name="menu" />
          </button>
          {companies.length > 1 ? (
            <Menu
              align="left"
              button={({ toggle, open }) => (
                <button type="button" className="company-switch" aria-expanded={open} aria-haspopup="menu" onClick={toggle}>
                  <span className="label">
                    <b>{company.name}</b>
                    <span>Switch company</span>
                  </span>
                  <Icon name="building" />
                  <Icon name="chevronDown" size={16} />
                </button>
              )}
            >
              {(close) => (
                <>
                  <div className="menu-label">Work in</div>
                  {companies.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={c.id === company.id}
                      aria-current={c.id === company.id}
                      onClick={() => {
                        close();
                        switchCompany(c.id);
                      }}
                    >
                      <span className="swatch" style={{ background: c.brandColor ?? "var(--color-inactive)" }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {c.name}
                        <span className="muted" style={{ display: "block", fontSize: "var(--text-small)", fontWeight: 400 }}>
                          {tradeWord(c.trade)}
                        </span>
                      </span>
                      {c.id === company.id ? <Icon name="check" size={18} /> : null}
                    </button>
                  ))}
                </>
              )}
            </Menu>
          ) : (
            // One company only: its name still shows here, with nothing to switch to.
            <div className="company-switch static">
              <span className="label">
                <b>{company.name}</b>
              </span>
            </div>
          )}
          <div className="spacer" />
          {crew ? null : (
            <label className="search">
              <Icon name="search" size={18} />
              <input
                ref={search}
                placeholder="Search"
                aria-label="Search"
                readOnly
                value=""
                onClick={() => setFinder({ open: true, start: "" })}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.ctrlKey || e.metaKey || e.altKey) return;
                  e.preventDefault();
                  setFinder({ open: true, start: e.key.length === 1 ? e.key : "" });
                }}
              />
              <kbd>Ctrl K</kbd>
            </label>
          )}
          {!crew ? <InboxButton /> : null}
          {crew ? <BellButton /> : <FeedButton />}
          {/* Jobber's gear: who is signed in, then Settings, the team, language, the look and Log out. */}
          <Menu
            button={({ toggle, open }) => (
              <button type="button" className="iconbtn" aria-label="Settings" aria-expanded={open} onClick={toggle}>
                <Icon name="settings" />
              </button>
            )}
          >
            {(close) => (
              <>
                <button
                  type="button"
                  className="menu-me"
                  onClick={() => {
                    close();
                    setPhotoOpen(true);
                  }}
                >
                  <Avatar name={me.name} {...(mine.data ? { person: mine.data } : {})} />
                  <div>
                    <b>{me.name}</b>
                    <span>{words.pictureAndColor}</span>
                  </div>
                </button>
                <hr />
                {crew ? null : (
                  <Link to="/settings" onClick={close}>
                    <Icon name="settings" size={18} />
                    Settings
                  </Link>
                )}
                {!crew && can("people.read") ? (
                  <Link to="/hr/team" onClick={close}>
                    <Icon name="team" size={18} />
                    Manage team
                  </Link>
                ) : null}
                <Link to="/settings/security" onClick={close}>
                  <Icon name="shield" size={18} />
                  {crew ? words.security : "Security and devices"}
                </Link>
                <LanguageItem />
                <ThemeItem />
                <MapsItem es={me.locale === "es"} />
                <hr />
                <button type="button" onClick={() => void signOut()}>
                  <Icon name="signout" size={18} />
                  {words.signOut}
                </button>
              </>
            )}
          </Menu>
        </header>
        <ProfilePhotoModal open={photoOpen} onClose={() => setPhotoOpen(false)} />
        {!crew && !clientAccount && can("client.update") ? <CreateFab /> : null}
        <BottomTabs crew={crew} onMore={() => setMenuOpen(true)} moreLabel={crew ? words.more : "More"} />
        <main className="content" id="main">
          {clientAccount ? <WorkingFor /> : null}
          <Outlet />
          {crew ? null : (
            <CommandMenu open={finder.open} start={finder.start} onClose={() => setFinder({ open: false, start: "" })} />
          )}
        </main>
      </div>
    </div>
  );
}

/** Phone tabs along the bottom, as in Jobber's app: the everyday screens, and More for the full menu. */
function BottomTabs({ crew, onMore, moreLabel }: { crew: boolean; onMore: () => void; moreLabel: string }) {
  const { can, me, company } = useSession();
  const access = useMyPayAccess(company.id, can("mypay.read"));
  const path = useRouterState({ select: (s) => s.location.pathname });
  const words = crewWords(me.locale);
  const tabs: NavItem[] = crew
    ? [
        { to: "/my-day", label: words.home, icon: "home" },
        { to: "/my-schedule", label: words.schedule, icon: "schedule" },
        ...(access.data?.linked && access.data.on
          ? [{ to: "/my-pay", label: payWords(me.locale).myPay, icon: "money" } as const]
          : []),
      ]
    : isClientAccount(company.trade)
      ? [{ to: "/marketing", label: "Marketing", icon: "marketing", action: "client.bulk" }]
      : company.trade === "agency"
        ? [
            { to: "/home", label: "Home", icon: "home", action: "company.read" },
            { to: "/deals", label: "Deals", icon: "pipeline", action: "lead.read" },
            { to: "/tasks", label: "Tasks", icon: "checklist", action: "client.read" },
            { to: "/clients", label: "Clients", icon: "clients", action: "client.read" },
          ]
        : [
            { to: "/home", label: "Home", icon: "home", action: "company.read" },
            { to: "/schedule", label: "Schedule", icon: "schedule", action: "schedule.read", notHq: true },
            // Search in place of Clients and Invoices, like Jobber (owner, 2026-10-30 and 2026-11-02); both are chips in
            // Search and stay in the menu, where Invoices keeps its To check count.
            { to: "/search", label: "Search", icon: "search", action: "client.read", notHq: true },
          ];
  return (
    <nav className="tabbar" aria-label="Tabs">
      {tabs
        .filter((t) => (!t.action || can(t.action)) && !(t.notHq && company.level === "hq"))
        .map((t) => (
          <Link key={t.to} to={t.to} className="tab" data-status={path === t.to ? "active" : undefined}>
            <Icon name={t.icon} size={22} />
            <span>{t.label}</span>
          </Link>
        ))}
      <button type="button" className="tab" onClick={onMore}>
        <Icon name="more" size={22} />
        <span>{moreLabel}</span>
      </button>
    </nav>
  );
}

/** The side menu shrunk to icons (as in Jobber), remembered on this browser. Phones always get the full slide-in menu. */
function readSlim(): boolean {
  try {
    return localStorage.getItem("steward.menu") === "slim";
  } catch {
    return false;
  }
}

/** "Olivia Grant" to "Olivia G.": the signed-in person at the top of the side menu (the company shows in the top bar). */
function personShort(name: string): string {
  const [first = name, ...rest] = name.trim().split(/\s+/);
  return shortName(first, rest.at(-1));
}

export function tradeWord(trade: Trade): string {
  return {
    cleaning: "House and office cleaning",
    lawn: "Lawn care",
    washers: "Pressure washing",
    agency: "Marketing agency",
    hq: "Head office",
    client: "Marketing client",
  }[trade];
}

/** Light, dark or the device's setting. */
function ThemeItem() {
  const read = () => document.documentElement.dataset.theme ?? "system";
  const [theme, setTheme] = useState(read);
  const next = theme === "system" ? "light" : theme === "light" ? "dark" : "system";
  return (
    <button
      type="button"
      onClick={() => {
        if (next === "system") delete document.documentElement.dataset.theme;
        else document.documentElement.dataset.theme = next;
        try {
          localStorage.setItem("steward.theme", next);
        } catch {
          // Not saved; the choice lasts until the page closes.
        }
        setTheme(next);
      }}
    >
      <Icon name={theme === "dark" ? "moon" : "sun"} size={18} />
      Look {theme === "system" ? "matches this device" : theme}
    </button>
  );
}

/** Which maps app Directions opens. Shown once Don't ask again is on; a tap brings the question back. */
function MapsItem({ es }: { es: boolean }) {
  const [pick, setPick] = useState(readMapsPick);
  if (!pick?.always) return null;
  return (
    <button
      type="button"
      onClick={() => {
        saveMapsPick({ app: pick.app, always: false });
        setPick(null);
      }}
    >
      <Icon name="directions" size={18} />
      {es ? "Preguntar mapas" : "Ask for maps"}
      <span className="menu-value">{MAPS_NAMES[pick.app].replace(" Maps", "")}</span>
    </button>
  );
}

/** English or Spanish for this login: the crew screens and their own messages (office screens stay English). */
function LanguageItem() {
  const { me } = useSession();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const words = crewWords(me.locale);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void api("POST", "auth/language", { locale: me.locale === "es" ? "en" : "es" })
          .catch(() => null)
          .then(() => qc.invalidateQueries({ queryKey: ["me"] }))
          .finally(() => setBusy(false));
      }}
    >
      <Icon name="message" size={18} />
      {words.language}
      <span className="menu-value">{words.languageName}</span>
    </button>
  );
}

/** The office bell: opens the activity feed. The count is this login's new alerts; opening the feed marks them read. */
function FeedButton() {
  const alerts = useAlerts();
  const read = useReadAlerts();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const n = alerts.data?.unread ?? 0;
  return (
    <>
      <button
        type="button"
        className="iconbtn inbox-btn"
        aria-label={n ? `Activity feed, ${String(n)} new` : "Activity feed"}
        aria-expanded={open}
        data-status={open ? "active" : undefined}
        onClick={() => {
          setOpen(!open);
          if (!open && n) read.mutate(undefined);
        }}
      >
        <Icon name="bell" />
        {n ? (
          <span key={n} className="inbox-count num">
            {n > 99 ? "99+" : n}
          </span>
        ) : null}
      </button>
      <ActivityFeed open={open} onClose={close} />
    </>
  );
}

/** The crew bell: alerts for this login, newest first. Opening it marks them read. */
function BellButton() {
  const alerts = useAlerts();
  const read = useReadAlerts();
  const n = alerts.data?.unread ?? 0;
  const list = alerts.data?.alerts ?? [];
  return (
    <Menu
      align="right"
      button={({ toggle, open }) => (
        <button
          type="button"
          className="iconbtn inbox-btn"
          aria-label={n ? `Notifications, ${String(n)} new` : "Notifications"}
          aria-expanded={open}
          onClick={() => {
            toggle();
            if (!open && n) read.mutate(undefined);
          }}
        >
          <Icon name="bell" />
          {n ? (
            <span key={n} className="inbox-count num">
              {n > 99 ? "99+" : n}
            </span>
          ) : null}
        </button>
      )}
    >
      {(close) =>
        list.length ? (
          <div className="bell-list">
            {list.slice(0, 20).map((a) =>
              a.link ? (
                <Link key={a.id} to={a.link} onClick={close} className={a.read ? "" : "unread"}>
                  <b>{a.title}</b>
                  {a.body ? <span className="muted small">{a.body}</span> : null}
                </Link>
              ) : (
                <div key={a.id} className={`menu-label${a.read ? "" : " unread"}`}>
                  <b>{a.title}</b>
                  {a.body ? <span className="muted small"> {a.body}</span> : null}
                </div>
              ),
            )}
          </div>
        ) : (
          <div className="menu-label">Nothing new.</div>
        )
      }
    </Menu>
  );
}

/** On Invoices in the menu and the phone tabs: how many wait in To check, like unread email. */
function ToCheckCount() {
  const { can, company } = useSession();
  const n = useToCheckCount(can("invoice.read") && company.level !== "hq").data?.count ?? 0;
  return n ? (
    <span className="inbox-count num nav-count" aria-label={`${String(n)} to check`}>
      {n > 99 ? "99+" : n}
    </span>
  ) : null;
}

/** The Inbox icon left of the bell, with how many conversations are unread. */
function InboxButton() {
  const unread = useInboxUnread();
  const n = unread.data?.count ?? 0;
  return (
    <Link to="/inbox" className="iconbtn inbox-btn" aria-label={n ? `Inbox, ${String(n)} unread` : "Inbox"}>
      <Icon name="message" />
      {n ? (
        <span key={n} className="inbox-count num">
          {n > 99 ? "99+" : n}
        </span>
      ) : null}
    </Link>
  );
}

/** "Olivia Gray" to "OG", for the square in the top left (owner, question 62). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")).toUpperCase() || "?";
}

/**
 * Create, as in Jobber: a menu row with a circled plus. It opens a card beside the menu with one colored icon per thing
 * to make (Client, Request, Quote, Job, Invoice), in the Home workflow colors. The card is fixed to the screen so the
 * side menu's scroll does not clip it; on phones it opens under the button. C opens it from anywhere.
 */
function CreateMenu({ slim }: { slim: boolean }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [at, setAt] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const close = () => setAt(null);
  const toggle = () => {
    if (at) {
      close();
      return;
    }
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerWidth - r.right < 420;
    // Never off the left edge, even if the phone menu is still sliding in.
    const left = Math.max(8, below ? r.left : r.right + 12);
    setAt(below ? { left, top: r.bottom + 8, below } : { left, top: r.top - 6, below });
  };
  useEffect(close, [path]);
  useEffect(() => {
    if (!at) return;
    card.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!card.current?.contains(t) && !btn.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        btn.current?.focus();
      }
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
    };
  }, [at]);
  const items = useCreateItems();
  // Left and right arrows walk the row, like Jobber's.
  const onArrow = (e: ReactKeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const all = [...(card.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const i = all.indexOf(document.activeElement as HTMLElement);
    all[(i + (e.key === "ArrowRight" ? 1 : all.length - 1)) % all.length]?.focus();
    e.preventDefault();
  };
  return (
    <div className="create">
      <button
        ref={btn}
        type="button"
        className="navlink create-btn"
        aria-haspopup="menu"
        aria-expanded={Boolean(at)}
        aria-keyshortcuts="c"
        data-create
        data-tip={slim ? "Create, key C" : "Shortcut key C"}
        data-status={at ? "active" : undefined}
        onClick={toggle}
      >
        <Icon name={at ? "closeCircle" : "plusCircle"} />
        <span className="label">Create</span>
      </button>
      {at ? (
        <div
          ref={card}
          className={`create-card${at.below ? " below" : ""}`}
          role="menu"
          aria-label="Create"
          style={{ left: unzoom(at.left), top: unzoom(at.top) }}
          onKeyDown={onArrow}
        >
          {items.map((x) => (
            <button
              key={x.label}
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                x.go();
              }}
            >
              <span style={{ color: x.color }}>
                <Icon name={x.icon} size={22} />
              </span>
              {x.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The round yellow button on a phone, like the old Steward's: on the main screens (not inside a form), it opens what
 * Create makes as a list above it. Bigger screens use Create in the side menu.
 */
function CreateFab() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const items = useCreateItems();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  // Only on a list or Home (one level deep), so it never covers a form's Save.
  if (path.split("/").filter(Boolean).length !== 1 || !items.length) return null;
  return (
    <div className="fab-wrap" ref={box}>
      {open ? (
        <div className="fab-menu" role="menu" aria-label="Create">
          {items.map((x) => (
            <button
              key={x.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                x.go();
              }}
            >
              <span style={{ color: x.color }}>
                <Icon name={x.icon} size={20} />
              </span>
              {x.label}
            </button>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="fab"
        aria-label={open ? "Close" : "Create"}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        <Icon name={open ? "close" : "plus"} size={26} />
      </button>
    </div>
  );
}

/** What Create makes, for the side menu's card and the phone's round button: only what this person may make. */
function useCreateItems(): { label: string; icon: IconName; color: string; go: () => void }[] {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const work = company.trade !== "agency";
  return [
    { label: "Client", icon: "clients", color: "var(--color-text)", go: () => void navigate({ to: "/clients/new" }) },
    ...(work && can("lead.update")
      ? [
          {
            label: "Request",
            icon: "requests" as const,
            color: "var(--color-flow-request)",
            go: () => void navigate({ to: "/requests", search: { new: true } }),
          },
        ]
      : []),
    ...(work && can("quote.update")
      ? [
          {
            label: "Quote",
            icon: "quotes" as const,
            color: "var(--color-flow-quote)",
            go: () => void navigate({ to: "/quotes/new", search: {} }),
          },
        ]
      : []),
    ...(work && can("job.update")
      ? [
          {
            label: "Job",
            icon: "jobs" as const,
            color: "var(--color-flow-job)",
            go: () => void navigate({ to: "/jobs/new", search: {} }),
          },
        ]
      : []),
    ...(work && can("invoice.update")
      ? [
          {
            label: "Invoice",
            icon: "invoices" as const,
            color: "var(--color-flow-invoice)",
            go: () => void navigate({ to: "/invoices", search: { new: true } }),
          },
        ]
      : []),
  ];
}
